import React from "react";

/**
 * Low-poly wireframe sphere — pure SVG, animated rotation.
 * Inspired by the "Coming Soon" reference: faint white lines forming
 * an icosahedron/geodesic mesh.
 */
export default function PolySphere({ size = 320, className = "", color = "rgba(255,255,255,0.35)", dotColor = "rgba(255,255,255,0.7)" }) {
  // Pre-computed vertices of a subdivided icosahedron projected to 2D ellipse
  const cx = size / 2;
  const cy = size / 2;
  const rx = size * 0.46;
  const ry = size * 0.42;

  // Generate points on a lat/lon grid for a wireframe effect
  const latLines = 8;
  const lonLines = 14;
  const points = [];
  for (let i = 1; i < latLines; i++) {
    const phi = (Math.PI * i) / latLines; // 0..PI
    const row = [];
    for (let j = 0; j < lonLines; j++) {
      const theta = (2 * Math.PI * j) / lonLines;
      // 3D unit sphere
      const x3 = Math.sin(phi) * Math.cos(theta);
      const y3 = Math.cos(phi);
      const z3 = Math.sin(phi) * Math.sin(theta);
      // tilt sphere slightly
      const tilt = -0.25;
      const yt = y3 * Math.cos(tilt) - z3 * Math.sin(tilt);
      const zt = y3 * Math.sin(tilt) + z3 * Math.cos(tilt);
      const depth = (zt + 1) / 2; // 0..1 (back -> front)
      const px = cx + x3 * rx;
      const py = cy + yt * ry;
      row.push({ id: `p-${i}-${j}`, x: px, y: py, depth });
    }
    points.push(row);
  }

  // Build edges: each point connects to next-in-row and same-col-in-next-row
  const lines = [];
  for (let i = 0; i < points.length; i++) {
    const row = points[i];
    for (let j = 0; j < row.length; j++) {
      const a = row[j];
      const b = row[(j + 1) % row.length];
      lines.push({ id: `h-${i}-${j}`, a, b, depth: (a.depth + b.depth) / 2 });
      if (i < points.length - 1) {
        const c = points[i + 1][j];
        lines.push({ id: `v-${i}-${j}`, a, b: c, depth: (a.depth + c.depth) / 2 });
      }
    }
  }
  // Some diagonal cross-edges for the "triangulated" low-poly feel
  for (let i = 0; i < points.length - 1; i++) {
    for (let j = 0; j < points[i].length; j++) {
      const a = points[i][j];
      const c = points[i + 1][(j + 1) % points[i].length];
      lines.push({ id: `d-${i}-${j}`, a, b: c, depth: (a.depth + c.depth) / 2 });
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      style={{ animation: "spinY 60s linear infinite" }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="poly-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0.03)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={rx} fill="url(#poly-glow)" />
      {lines.map((l) => (
        <line
          key={`l-${l.id}`}
          x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y}
          stroke={color}
          strokeWidth={0.4 + l.depth * 0.5}
          opacity={0.15 + l.depth * 0.55}
        />
      ))}
      {points.flat().map((p) => (
        <circle key={`d-${p.id}`} cx={p.x} cy={p.y} r={0.6 + p.depth * 1.2}
                fill={dotColor} opacity={0.3 + p.depth * 0.7} />
      ))}
    </svg>
  );
}
